import { type TFunction } from 'i18next';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type PeriodKind, periodOf, periodRange, shiftPeriod } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { cn } from '../../../lib/utils';

const KINDS: readonly PeriodKind[] = ['month', 'quarter', 'year'];

/**
 * Chọn kỳ cho dự án bảo trì — APM-MAINT-e.
 *
 * ## Vì sao dự án bảo trì cần thứ này
 *
 * Agile chia theo sprint, Waterfall theo pha — cả hai đều có điểm kết thúc, nên
 * "cả dự án" là câu hỏi trả lời được. Dự án bảo trì chạy tới khi PM dừng
 * (APM-MAINT-b), nên sau ba năm "cả dự án" là hai nghìn việc không có điểm
 * dừng, và mọi báo cáo trên đó gộp cả ba năm vào một con số.
 *
 * Kỳ lịch là cách chia tự nhiên của công việc vận hành: khớp chu kỳ báo cáo cho
 * khách hàng và chu kỳ ngân sách, và **không cần ai lập kế hoạch để tồn tại** —
 * khác sprint, thứ phải được tạo ra trước khi dùng được.
 *
 * ## Vì sao có hai nút mũi tên
 *
 * Câu hỏi thường gặp nhất là "so với kỳ trước thì sao", và trả lời nó bằng một
 * ô chọn nghĩa là mở danh sách, tìm đúng mục, bấm — rồi lặp lại để quay về. Hai
 * nút biến việc ấy thành một cú bấm mỗi chiều.
 *
 * ## Vì sao đổi loại kỳ thì giữ nguyên mốc thời gian
 *
 * Đang xem tháng 8/2026 mà bấm "Quý" thì ra Q3/2026 — quý **chứa** tháng đang
 * xem, không phải quý hiện tại. Nhảy về hôm nay là mất chỗ đang đứng, và người
 * dùng phải lần lại từ đầu chỉ vì muốn nhìn rộng ra một bậc.
 */
export function PeriodPicker({
  value,
  onChange,
  disabled = false,
}: {
  /** Mã kỳ đang xem (`2026-08`, `2026-Q3`, `2026`). */
  readonly value: string;
  readonly onChange: (period: string) => void;
  /**
   * Khoá chứ không ẩn — cách nhìn Định kỳ của `TasksPanel`. Ẩn đi thì mọi thứ
   * bên dưới nhảy lên một hàng mỗi lần đổi cách nhìn.
   */
  readonly disabled?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const range = periodRange(value);
  if (!range) return null;

  /** Ngày giữa kỳ — mốc để đổi sang loại kỳ khác mà không trôi sang kỳ bên cạnh. */
  const anchor = new Date(`${range.from}T00:00:00Z`);

  return (
    <span
      className={cn('flex flex-wrap items-center gap-1.5', disabled && 'opacity-50')}
      aria-disabled={disabled || undefined}
    >
      <span aria-hidden className="text-xs text-muted-foreground">
        {t('period.label')}
      </span>

      {/*
        Ba nút loại kỳ thay vì một `<select>`: chỉ có ba lựa chọn, và ở dạng nút
        thì người dùng thấy **hết** các cách nhìn có sẵn mà không phải mở ra
        xem. Cùng lý do với hàng nút chọn mức phóng của Gantt.
      */}
      <GlassSegmented
        size="sm"
        value={range.kind}
        disabled={disabled}
        options={KINDS.map((kind) => ({ value: kind, label: t(`period.kind.${kind}`) }))}
        onChange={(kind) => onChange(periodOf(anchor, kind))}
      />

      <Button
        type="button"
        size="sm"
        variant="ghost"
        aria-label={t('period.previous')}
        disabled={disabled}
        onClick={() => {
          const next = shiftPeriod(value, -1);
          if (next) onChange(next);
        }}
      >
        <ChevronLeft aria-hidden className="size-4" />
      </Button>

      {/* `tabular-nums`: nhãn đổi độ rộng mỗi lần bấm mũi tên thì hai nút nhảy
          qua lại dưới con trỏ, và cú bấm thứ hai trượt ra ngoài. */}
      <span className="min-w-20 text-center text-sm font-medium tabular-nums">
        {label(value, range.kind, t)}
      </span>

      <Button
        type="button"
        size="sm"
        variant="ghost"
        aria-label={t('period.next')}
        disabled={disabled}
        onClick={() => {
          const next = shiftPeriod(value, 1);
          if (next) onChange(next);
        }}
      >
        <ChevronRight aria-hidden className="size-4" />
      </Button>
    </span>
  );
}

/**
 * Nhãn đọc được: `Tháng 8/2026`, `Quý 3/2026`, `Năm 2026`.
 *
 * Không hiện thẳng mã kỳ: `2026-08` đọc được nhưng `2026-Q3` thì phải biết quy
 * ước mới hiểu, và mã ấy vốn sinh ra để đi trên URL chứ không để đọc.
 */
function label(period: string, kind: PeriodKind, t: TFunction): string {
  const year = period.slice(0, 4);
  if (kind === 'year') return t('period.yearLabel', { year });
  if (kind === 'quarter') return t('period.quarterLabel', { quarter: period.slice(6), year });
  return t('period.monthLabel', { month: Number(period.slice(5)), year });
}
